import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Sparkles, MapPin, Phone, Instagram, Utensils, Clock, MessageCircle } from "lucide-react";
import { CONTACT } from "@/veedu/data";
import { useReservation } from "@/context/ReservationContext";
import { scrollToId } from "@/hooks/useLenis";

const ComingSoonModal = () => {
  const { isReservationModalOpen, closeReservationModal, modalData } = useReservation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeReservationModal();
      }
    };

    if (isReservationModalOpen) {
      window.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isReservationModalOpen, closeReservationModal]);

  const handleExploreMenu = () => {
    closeReservationModal();
    if (location.pathname !== "/") {
      navigate("/#table");
      setTimeout(() => {
        scrollToId("table");
      }, 200);
    } else {
      scrollToId("table");
    }
  };

  return (
    <AnimatePresence>
      {isReservationModalOpen && (
        <div
          data-testid="coming-soon-modal-backdrop"
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
        >
          {/* Backdrop Blur Layer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeReservationModal}
            className="fixed inset-0 bg-black/85 backdrop-blur-md"
          />

          {/* Dialog Container */}
          <motion.div
            data-testid="coming-soon-modal-content"
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-3xl border border-[#C2A04A]/30 bg-[#120B07] text-[#F4ECD8] p-6 sm:p-8 md:p-10 shadow-[0_25px_70px_rgba(0,0,0,0.9)] z-10 no-scrollbar"
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 0%, rgba(194, 160, 74, 0.15), transparent 70%), radial-gradient(circle at 100% 100%, rgba(168, 50, 29, 0.08), transparent 50%)",
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              data-testid="coming-soon-close-btn"
              onClick={closeReservationModal}
              aria-label="Close dialog"
              className="absolute top-4 right-4 sm:top-6 sm:right-6 flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full border border-[#C2A04A]/30 bg-black/40 text-[#F4ECD8]/70 transition-all duration-300 hover:border-[#C2A04A] hover:text-[#C2A04A] hover:bg-[#C2A04A]/10 hover:scale-105 active:scale-95 cursor-pointer"
            >
              <X size={18} strokeWidth={2} />
            </button>

            {/* Top Opening Status Pill */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#C2A04A]/40 bg-[#C2A04A]/10 text-[#C2A04A] text-[10px] font-body tracking-[0.25em] uppercase font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-[#C2A04A] animate-pulse" />
                OPENING SOON • HORNCHURCH
              </span>
            </div>

            {/* Wordmark & Heritage Subtitle */}
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-display text-2xl sm:text-3xl tracking-[0.16em] text-[#F4ECD8]">
                #VEEDU
              </span>
              <span className="font-mal text-sm sm:text-base text-[#C2A04A]/80">
                വീട്
              </span>
            </div>

            {/* Main Headline */}
            <h2 className="font-display text-2xl sm:text-3xl md:text-[32px] font-light text-[#F4ECD8] tracking-tight leading-snug mb-3">
              Table Reservations Opening Soon
            </h2>

            {/* Warm Descriptive Narrative */}
            <p className="font-body text-xs sm:text-sm text-[#D3C1AF]/85 leading-relaxed mb-6 font-normal">
              Chef Jomon and our culinary team are putting the final touches on our dining halls, spice cellar, and kitchen at 197–201 High Street in Hornchurch. Our doors are not open yet, but table reservations will go live shortly ahead of our official opening date.
            </p>

            {/* Optional Context from Dish Modal if available */}
            {modalData?.dishName && (
              <div className="mb-5 px-3.5 py-2.5 rounded-xl border border-[#C2A04A]/25 bg-black/30 flex items-center gap-2.5 text-xs text-[#F4ECD8]">
                <Sparkles size={14} className="text-[#C2A04A] shrink-0" />
                <span>
                  Interested in experiencing{" "}
                  <strong className="text-[#C2A04A] font-semibold">{modalData.dishName}</strong>? Pre-order and preview tastings will be announced soon.
                </span>
              </div>
            )}

            {/* Key Information Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {/* Location Card */}
              <a
                href={CONTACT.maps}
                target="_blank"
                rel="noreferrer"
                className="group flex flex-col justify-between p-3.5 rounded-xl border border-white/10 bg-white/[0.03] hover:border-[#C2A04A]/50 hover:bg-[#C2A04A]/5 transition-all text-left"
              >
                <div className="flex items-center gap-2 text-[#C2A04A] text-xs font-semibold uppercase tracking-wider mb-1">
                  <MapPin size={13} className="shrink-0" />
                  <span>Location</span>
                </div>
                <p className="text-[11px] text-[#D3C1AF]/90 leading-tight">
                  197–201 High Street, Hornchurch, Essex RM11 3XT
                </p>
                <span className="text-[10px] text-[#C2A04A] group-hover:underline mt-2 inline-flex items-center gap-1 font-body tracking-wider">
                  View on Google Maps ↗
                </span>
              </a>

              {/* Planned Service Hours Card */}
              <div className="flex flex-col justify-between p-3.5 rounded-xl border border-white/10 bg-white/[0.03]">
                <div className="flex items-center gap-2 text-[#C2A04A] text-xs font-semibold uppercase tracking-wider mb-1">
                  <Clock size={13} className="shrink-0" />
                  <span>Planned Service Hours</span>
                </div>
                <p className="text-[11px] text-[#D3C1AF]/90 leading-tight">
                  Tue–Fri: 16:30–23:00 <br />
                  Sat–Sun: 13:00–23:00 • Mon: Closed
                </p>
                <span className="text-[10px] text-[#D3C1AF]/60 mt-2 font-body tracking-wider">
                  Grand Opening Date: Coming Soon
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              {/* Pre-Opening WhatsApp Enquiries */}
              <a
                href={CONTACT.whatsappPreOpening || CONTACT.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="coming-soon-whatsapp-btn"
                className="flex-1 inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-full bg-[#C2A04A] text-[#120B07] hover:bg-[#D4B35D] font-body text-[11px] tracking-[0.2em] uppercase font-bold transition-all shadow-[0_8px_25px_rgba(194,160,74,0.3)] hover:-translate-y-0.5"
              >
                <MessageCircle size={15} className="fill-current" />
                <span>Pre-Opening Enquiries</span>
              </a>

              {/* Explore Menu Button */}
              <button
                type="button"
                onClick={handleExploreMenu}
                data-testid="coming-soon-menu-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/25 text-[#F4ECD8] hover:border-[#C2A04A] hover:text-[#C2A04A] hover:bg-[#C2A04A]/10 font-body text-[11px] tracking-[0.2em] uppercase font-medium transition-all"
              >
                <Utensils size={14} />
                <span>Explore The Menu</span>
              </button>
            </div>

            {/* Quick Contact & Social Strip */}
            <div className="mt-6 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-[10px] sm:text-[11px] text-[#D3C1AF]/70 font-body">
              <a
                href={CONTACT.phoneHref}
                className="flex items-center gap-1.5 hover:text-[#C2A04A] transition-colors"
              >
                <Phone size={12} className="text-[#C2A04A]" />
                <span>{CONTACT.phone}</span>
              </a>

              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 hover:text-[#C2A04A] transition-colors"
              >
                <Instagram size={12} className="text-[#C2A04A]" />
                <span>Follow @chefjomon for opening updates</span>
              </a>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ComingSoonModal;
